import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as signalR from '@microsoft/signalr';
import {
  getCurrentUser,
  getUserSettings,
  updateUserSettings,
  getModels,
  getChatList,
  createChat,
  deleteChat,
  archiveChat,
  getChatById,
} from '../services/api';
import { initSocket, disconnectSocket } from '../services/socket';
import type {
  User,
  Model,
  Chat,
  Config,
  Settings,
  Folder,
  Tag,
  Tool,
  Banner,
  Knowledge,
} from '../types';

// UI Store
interface UIState {
  showSidebar: boolean;
  showRightPanel: boolean;
  currentView: string;
  showSettings: boolean;
  showSearch: boolean;
  showArchivedChats: boolean;
  showChangelog: boolean;
  showControls: boolean;
  showEmbeds: boolean;
  showOverview: boolean;
  showArtifacts: boolean;
  showCallOverlay: boolean;
  showFileNav: boolean;
  mobile: boolean;
  tablet: boolean;
  sidebarWidth: number;
  setShowSidebar: (show: boolean) => void;
  setShowRightPanel: (show: boolean) => void;
  setCurrentView: (view: string) => void;
  setShowSettings: (show: boolean) => void;
  setShowSearch: (show: boolean) => void;
  setShowArchivedChats: (show: boolean) => void;
  setShowChangelog: (show: boolean) => void;
  setShowControls: (show: boolean) => void;
  setShowEmbeds: (show: boolean) => void;
  setShowOverview: (show: boolean) => void;
  setShowArtifacts: (show: boolean) => void;
  setShowCallOverlay: (show: boolean) => void;
  setShowFileNav: (show: boolean) => void;
  setMobile: (mobile: boolean) => void;
  setTablet: (tablet: boolean) => void;
  setSidebarWidth: (width: number) => void;
}

export const useUIStore = create<UIState>((set) => ({
  showSidebar: false,
  showRightPanel: false,
  currentView: 'dashboard',
  showSettings: false,
  showSearch: false,
  showArchivedChats: false,
  showChangelog: false,
  showControls: false,
  showEmbeds: false,
  showOverview: false,
  showArtifacts: false,
  showCallOverlay: false,
  showFileNav: false,
  mobile: false,
  tablet: false,
  sidebarWidth: 260,
  setShowSidebar: (show) => set({ showSidebar: show }),
  setShowRightPanel: (show) => set({ showRightPanel: show }),
  setCurrentView: (view) => set({ currentView: view }),
  setShowSettings: (show) => set({ showSettings: show }),
  setShowSearch: (show) => set({ showSearch: show }),
  setShowArchivedChats: (show) => set({ showArchivedChats: show }),
  setShowChangelog: (show) => set({ showChangelog: show }),
  setShowControls: (show) => set({ showControls: show }),
  setShowEmbeds: (show) => set({ showEmbeds: show }),
  setShowOverview: (show) => set({ showOverview: show }),
  setShowArtifacts: (show) => set({ showArtifacts: show }),
  setShowCallOverlay: (show) => set({ showCallOverlay: show }),
  setShowFileNav: (show) => set({ showFileNav: show }),
  setMobile: (mobile) => set({ mobile }),
  setTablet: (tablet) => set({ tablet }),
  setSidebarWidth: (width) => set({ sidebarWidth: width }),
}));

// User Store
interface UserState {
  user: User | null | undefined;
  token: string | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  clearUser: () => void;
  syncUser: () => Promise<void>;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: undefined,
      token: null,
      loading: false,
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      clearUser: () => set({ user: null, token: null }),
      syncUser: async () => {
        const { token } = get();
        if (!token) {
          set({ user: null, loading: false });
          return;
        }

        set({ loading: true });
        try {

          const userData = await getCurrentUser();
          set({ user: userData, loading: false });
        } catch (error) {
          console.error('Failed to sync user:', error);
          // Keep existing user data if sync fails
          set({ loading: false });
        }
      },
    }),
    {
      name: 'mawi-chat-storage',
      partialize: (state) => ({ token: state.token }),
    }
  )
);

// Config Store
interface ConfigState {
  config: Config | null | undefined;
  setConfig: (config: Config | null) => void;
}

export const useConfigStore = create<ConfigState>((set) => ({
  config: undefined,
  setConfig: (config) => set({ config }),
}));

// Settings Store
interface SettingsState {
  settings: Settings;
  loading: boolean;
  error: string | null;
  setSettings: (settings: Settings) => void;
  updateSettings: (settings: Partial<Settings>) => void;
  loadSettings: () => Promise<void>;
  saveSettings: () => Promise<void>;
  clearError: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      settings: {
        theme: 'system',
        language: 'en',
        title: { auto: true },
        audio: { stt: null, tts: null },
        responseAutoCopy: false,
        responseAutoPlayback: false,
        hapticFeedback: false,
        ctrlEnterToSend: false,
        onboardingCompleted: false,
      },
      loading: false,
      error: null,
      setSettings: (settings) => set({ settings, error: null }),
      updateSettings: (settings) =>
        set((state) => ({ settings: { ...state.settings, ...settings } })),
      loadSettings: async () => {
        set({ loading: true, error: null });
        try {

          const backendSettings = await getUserSettings();
          if (backendSettings) {
            // Merge backend settings with defaults
            const mergedSettings = { ...get().settings, ...backendSettings };
            set({ settings: mergedSettings, loading: false });
          } else {
            set({ loading: false });
          }
        } catch (error) {
          console.error('Failed to load settings:', error);
          set({ loading: false, error: error instanceof Error ? error.message : 'Failed to load settings' });
        }
      },
      saveSettings: async () => {
        set({ loading: true, error: null });
        try {

          const currentSettings = get().settings;
          await updateUserSettings(currentSettings);
          set({ loading: false });
        } catch (error) {
          console.error('Failed to save settings:', error);
          set({ loading: false, error: error instanceof Error ? error.message : 'Failed to save settings' });
        }
      },
      clearError: () => set({ error: null }),
    }),
    {
      name: 'mawi-chat-settings',
    }
  )
);

// Models Store
interface ModelsState {
  models: Model[];
  loading: boolean;
  error: string | null;
  setModels: (models: Model[]) => void;
  getModelById: (id: string) => Model | undefined;
  loadModels: (refresh?: boolean) => Promise<void>;
  clearError: () => void;
}

export const useModelsStore = create<ModelsState>((set, get) => ({
  models: [],
  loading: false,
  error: null,
  setModels: (models) => set({ models, error: null }),
  getModelById: (id) => get().models.find((m) => m.id === id),
  loadModels: async (refresh = false) => {
    set({ loading: true, error: null });
    try {

      const models = await getModels(refresh);
      set({ models, loading: false });
    } catch (error) {
      console.error('Failed to load models:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to load models' });
    }
  },
  clearError: () => set({ error: null }),
}));

// Chats Store
interface ChatsState {
  chats: Chat[] | null;
  currentChat: Chat | null;
  currentChatId: string | null;
  chatTitle: string;
  pinnedChats: Chat[];
  tags: Tag[];
  folders: Folder[];
  selectedFolder: Folder | null;
  currentChatPage: number;
  loading: boolean;
  error: string | null;
  setChats: (chats: Chat[]) => void;
  setCurrentChat: (chat: Chat | null) => void;
  setCurrentChatId: (id: string | null) => void;
  setChatTitle: (title: string) => void;
  setPinnedChats: (chats: Chat[]) => void;
  setTags: (tags: Tag[]) => void;
  setFolders: (folders: Folder[]) => void;
  setSelectedFolder: (folder: Folder | null) => void;
  setCurrentChatPage: (page: number) => void;
  addChat: (chat: Chat) => void;
  updateChat: (id: string, updates: Partial<Chat>) => void;
  deleteChat: (id: string) => void;
  loadChats: (page?: number) => Promise<void>;
  createNewChat: (title?: string) => Promise<Chat | null>;
  deleteChatById: (id: string) => Promise<void>;
  archiveChatById: (id: string) => Promise<void>;
  loadChatById: (id: string) => Promise<Chat | null>;
  sendMessage: (chatId: string, message: string, model?: string, context?: any) => Promise<void>;
  clearError: () => void;
}

export const useChatsStore = create<ChatsState>((set, get) => ({
  chats: null,
  currentChat: null,
  currentChatId: null,
  chatTitle: '',
  pinnedChats: [],
  tags: [],
  folders: [],
  selectedFolder: null,
  currentChatPage: 1,
  loading: false,
  error: null,
  setChats: (chats) => set({ chats, error: null }),
  setCurrentChat: (chat) => set({ currentChat: chat }),
  setCurrentChatId: (id) => set({ currentChatId: id }),
  setChatTitle: (title) => set({ chatTitle: title }),
  setPinnedChats: (chats) => set({ pinnedChats: chats }),
  setTags: (tags) => set({ tags }),
  setFolders: (folders) => set({ folders }),
  setSelectedFolder: (folder) => set({ selectedFolder: folder }),
  setCurrentChatPage: (page) => set({ currentChatPage: page }),
  addChat: (chat) => {
    const chats = get().chats || [];
    set({ chats: [chat, ...chats] });
  },
  updateChat: (id, updates) => {
    const chats = get().chats || [];
    set({
      chats: chats.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    });
    if (get().currentChatId === id) {
      set({ currentChat: { ...get().currentChat!, ...updates } });
    }
  },
  deleteChat: (id) => {
    const chats = get().chats || [];
    set({ chats: chats.filter((c) => c.id !== id) });
  },
  loadChats: async (page = 1) => {
    set({ loading: true, error: null });
    try {

      const chats = await getChatList(page, 20);
      set({ chats, loading: false, currentChatPage: page });
    } catch (error) {
      console.error('Failed to load chats:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to load chats' });
    }
  },
  createNewChat: async (title) => {
    set({ loading: true, error: null });
    try {

      const chat = await createChat({ title });
      const chats = get().chats || [];
      set({ chats: [chat, ...chats], loading: false });
      return chat;
    } catch (error) {
      console.error('Failed to create chat:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to create chat' });
      return null;
    }
  },
  deleteChatById: async (id) => {
    set({ loading: true, error: null });
    try {

      await deleteChat(id);
      const chats = get().chats || [];
      set({ chats: chats.filter((c) => c.id !== id), loading: false });
      if (get().currentChatId === id) {
        set({ currentChat: null, currentChatId: null });
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to delete chat' });
    }
  },
  archiveChatById: async (id) => {
    set({ loading: true, error: null });
    try {

      await archiveChat(id);
      // Remove from active chats list
      const chats = get().chats || [];
      set({ chats: chats.filter((c) => c.id !== id), loading: false });
      if (get().currentChatId === id) {
        set({ currentChat: null, currentChatId: null });
      }
    } catch (error) {
      console.error('Failed to archive chat:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to archive chat' });
    }
  },
  loadChatById: async (id) => {
    set({ loading: true, error: null });
    try {

      const chat = await getChatById(id);
      set({ currentChat: chat, currentChatId: id, loading: false });
      return chat;
    } catch (error) {
      console.error('Failed to load chat:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to load chat' });
      return null;
    }
  },
  sendMessage: async (chatId, message, model, context) => {
    // This will be implemented with SignalR streaming
    set({ error: null });
    try {
      // Implementation will be added when we integrate with the chat component
      console.log('Sending message:', { chatId, message, model, context });
    } catch (error) {
      console.error('Failed to send message:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to send message' });
    }
  },
  clearError: () => set({ error: null }),
}));

// Tools Store
interface ToolsState {
  tools: Tool[] | null;
  setTools: (tools: Tool[]) => void;
}

export const useToolsStore = create<ToolsState>((set) => ({
  tools: null,
  setTools: (tools) => set({ tools }),
}));

// Knowledge Store
interface KnowledgeState {
  knowledge: Knowledge[] | null;
  setKnowledge: (knowledge: Knowledge[]) => void;
}

export const useKnowledgeStore = create<KnowledgeState>((set) => ({
  knowledge: null,
  setKnowledge: (knowledge) => set({ knowledge }),
}));

// Banners Store
interface BannersState {
  banners: Banner[];
  setBanners: (banners: Banner[]) => void;
}

export const useBannersStore = create<BannersState>((set) => ({
  banners: [],
  setBanners: (banners) => set({ banners }),
}));

// Chat Input Store
interface ChatInputState {
  prompt: string;
  files: any[];
  selectedToolIds: string[];
  webSearchEnabled: boolean;
  imageGenerationEnabled: boolean;
  codeInterpreterEnabled: boolean;
  setPrompt: (prompt: string) => void;
  setFiles: (files: any[]) => void;
  setSelectedToolIds: (ids: string[]) => void;
  setWebSearchEnabled: (enabled: boolean) => void;
  setImageGenerationEnabled: (enabled: boolean) => void;
  setCodeInterpreterEnabled: (enabled: boolean) => void;
  clearInput: () => void;
}

export const useChatInputStore = create<ChatInputState>((set) => ({
  prompt: '',
  files: [],
  selectedToolIds: [],
  webSearchEnabled: false,
  imageGenerationEnabled: false,
  codeInterpreterEnabled: false,
  setPrompt: (prompt) => set({ prompt }),
  setFiles: (files) => set({ files }),
  setSelectedToolIds: (ids) => set({ selectedToolIds: ids }),
  setWebSearchEnabled: (enabled) => set({ webSearchEnabled: enabled }),
  setImageGenerationEnabled: (enabled) => set({ imageGenerationEnabled: enabled }),
  setCodeInterpreterEnabled: (enabled) => set({ codeInterpreterEnabled: enabled }),
  clearInput: () =>
    set({
      prompt: '',
      files: [],
      selectedToolIds: [],
      webSearchEnabled: false,
      imageGenerationEnabled: false,
      codeInterpreterEnabled: false,
    }),
}));

// SignalR Store for real-time communication
interface SignalRState {
  connection: signalR.HubConnection | null;
  connectionState: signalR.HubConnectionState;
  connecting: boolean;
  error: string | null;
  setConnection: (connection: signalR.HubConnection | null) => void;
  setConnectionState: (state: signalR.HubConnectionState) => void;
  setConnecting: (connecting: boolean) => void;
  setError: (error: string | null) => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  clearError: () => void;
}

export const useSignalRStore = create<SignalRState>((set, get) => ({
  connection: null,
  connectionState: signalR.HubConnectionState.Disconnected,
  connecting: false,
  error: null,
  setConnection: (connection) => set({ connection }),
  setConnectionState: (state) => set({ connectionState: state }),
  setConnecting: (connecting) => set({ connecting }),
  setError: (error) => set({ error }),
  connect: async () => {

    set({ connecting: true, error: null });

    try {
      const connection = await initSocket();
      if (connection) {
        set({ connection, connectionState: connection.state, connecting: false });

        // Set up connection state change handler
        connection.onclose(() => {
          set({ connectionState: signalR.HubConnectionState.Disconnected });
        });

        connection.onreconnecting(() => {
          set({ connectionState: signalR.HubConnectionState.Reconnecting });
        });

        connection.onreconnected(() => {
          set({ connectionState: signalR.HubConnectionState.Connected });
        });
      } else {
        set({ connecting: false, error: 'Failed to initialize SignalR connection' });
      }
    } catch (error) {
      console.error('SignalR connection failed:', error);
      set({
        connecting: false,
        error: error instanceof Error ? error.message : 'Connection failed'
      });
    }
  },
  disconnect: async () => {

    await disconnectSocket();
    set({
      connection: null,
      connectionState: signalR.HubConnectionState.Disconnected,
      error: null
    });
  },
  clearError: () => set({ error: null }),
}));